import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { isValidConvexId } from "@plasma/utils/convex-id";
import { stripPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  employeesCheckIfPinInUse,
  employeesGetById,
  employeesUpdateById,
  locationsGetAllMine,
} from "~/api-refs";
import {
  EmployeeEmailField,
  EmployeeHourlyRateField,
  EmployeeLocationField,
  EmployeeLogoField,
  EmployeeNameField,
  EmployeePhoneField,
  EmployeePinField,
  EmployeeRolesField,
} from "~/components/employees/employee-fields";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { Skeleton } from "~/components/ui/skeleton";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { enumIncludes } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

// Helper function to get the error message to avoid nested ternary
const getErrorMessage = (validEmployeeId: boolean, validField: boolean) => {
  if (!validEmployeeId) {
    return "Invalid employee ID";
  }
  if (!validField) {
    return "Invalid field";
  }
  return "Employee not found";
};

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/employees/$id/edit/$field"
)({
  component: EditEmployeeFieldModal,
});

// Type for the form - matches Convex mutation exactly
interface EmployeeFormData {
  firstName: string;
  lastName: string;
  imageUrl?: string;
  email?: string;
  phoneNumber?: string;
  hourlyRate?: number;
  addToFutureLocations: boolean;
  pin: string;
  roles: Id<"organizationRoles">[];
  locations: Id<"locations">[];
}

const VALID_FIELDS = [
  "photo",
  "name",
  "phone",
  "email",
  "hourlyRate",
  "pin",
  "roles",
  "locations",
] as const;
type ValidField = (typeof VALID_FIELDS)[number];
const isValidEmployeeField = enumIncludes(VALID_FIELDS);

const FIELD_LABELS: Record<ValidField, string> = {
  photo: "Photo",
  name: "Name",
  phone: "Phone Number",
  email: "Email Address",
  hourlyRate: "Hourly Rate",
  pin: "PIN",
  roles: "Roles",
  locations: "Locations",
};

function EditEmployeeFieldModal() {
  const { id: employeeId, field } = Route.useParams();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate field parameter
  const validField = isValidEmployeeField(field) ? field : null;
  // Validate employee ID format
  const validEmployeeId = isValidConvexId(employeeId);

  const handleClose = () => {
    void navigate({ to: "/employees/$id", params: { id: employeeId } });
  };

  // Fetch employee data (only if ID is valid)
  const { data: employee, isLoading } = useQuery({
    ...convexQuery(employeesGetById, {
      id: toConvexTableId("employees", employeeId),
    }),
    enabled: validEmployeeId,
  });

  // Fetch all locations for location field
  const { data: allMerchantLocations = [] } = useQuery({
    ...convexQuery(locationsGetAllMine, {}),
    enabled: validField === "locations",
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: (
      data: { id: Id<"employees"> } & EmployeeFormData & {
          phoneNumber?: string;
        }
    ) => {
      return convexClient.mutation(employeesUpdateById, data);
    },
    onSuccess: (_result, variables) => {
      // Track employee update in PostHog
      ProductEvents.employeeUpdated(variables.id);
    },
  });

  const form = useForm<EmployeeFormData>({
    values: employee
      ? {
          firstName: employee.firstName ?? "",
          lastName: employee.lastName ?? "",
          imageUrl: employee.imageUrl,
          email: employee.email,
          phoneNumber: employee.phoneNumber,
          hourlyRate: employee.hourlyRate,
          pin: employee.pin ?? "",
          addToFutureLocations: employee.addToFutureLocations ?? false,
          roles: employee.employeeRoles.map((r) => r.roleId) ?? [],
          locations:
            employee.locationAssignments.map(
              (assignment) => assignment.locationId
            ) ?? [],
        }
      : undefined,
  });

  const onSubmit = async (data: EmployeeFormData) => {
    if (updateEmployeeMutation.isPending || isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    try {
      form.clearErrors();

      // Special handling for PIN field - check if in use
      if (validField === "pin") {
        const isPinInUse = await convexClient.query(employeesCheckIfPinInUse, {
          pin: data.pin,
        });
        if (isPinInUse) {
          form.setError("pin", { message: "Pin is in use" });
          setIsSubmitting(false);
          return;
        }
      }

      // Convert hourly rate from string to number
      let hourlyRateValue: number | undefined;
      if (data.hourlyRate) {
        hourlyRateValue =
          typeof data.hourlyRate === "string"
            ? Number.parseFloat(data.hourlyRate)
            : data.hourlyRate;
      }

      const { hourlyRate: _, ...restData } = data;

      await updateEmployeeMutation.mutateAsync({
        id: toConvexTableId("employees", employeeId),
        ...restData,
        phoneNumber: data.phoneNumber
          ? stripPhoneNumberMask(data.phoneNumber)
          : undefined,
        hourlyRate: hourlyRateValue,
      });

      handleClose();
    } catch {
      // Error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormField = () => {
    if (!validField) {
      return null;
    }

    switch (validField) {
      case "photo":
        return (
          <EmployeeLogoField
            control={form.control}
            name="imageUrl"
            setUploading={setIsUploading}
          />
        );
      case "name":
        return (
          <EmployeeNameField
            control={form.control}
            firstNameField="firstName"
            lastNameField="lastName"
          />
        );
      case "phone":
        return <EmployeePhoneField control={form.control} name="phoneNumber" />;
      case "email":
        return <EmployeeEmailField control={form.control} name="email" />;
      case "hourlyRate":
        return (
          <EmployeeHourlyRateField control={form.control} name="hourlyRate" />
        );
      case "pin":
        return <EmployeePinField control={form.control} name="pin" />;
      case "roles":
        return <EmployeeRolesField control={form.control} name="roles" />;
      case "locations":
        return (
          <EmployeeLocationField
            control={form.control}
            locations={allMerchantLocations}
            name="locations"
            onAllLocationsSelectedChange={(allSelected) =>
              form.setValue("addToFutureLocations", allSelected)
            }
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent
          className="max-h-full scrollbar-none overflow-y-scroll p-6"
          noScrollPrevention
        >
          <Skeleton className="h-48 w-full" />
        </DialogContent>
      </Dialog>
    );
  }

  if (!(employee && validField && validEmployeeId)) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent
          className="max-h-full scrollbar-none overflow-y-scroll p-6"
          noScrollPrevention
        >
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-muted-foreground">
              {getErrorMessage(validEmployeeId, validField !== null)}
            </p>
            <Button
              className="mt-4"
              onClick={handleClose}
              type="button"
              variant="outline"
            >
              Back to Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isPending =
    isSubmitting ||
    updateEmployeeMutation.isPending ||
    (validField === "photo" && isUploading);

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent
        className="max-h-full scrollbar-none overflow-y-scroll p-6"
        noScrollPrevention
      >
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogTitle className="text-center">
              Update {FIELD_LABELS[validField].toLowerCase()}
            </DialogTitle>
            {renderFormField()}
            <DialogFooter>
              <Button
                className="w-full"
                onClick={handleClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={isPending}
                size="lg"
                type="submit"
              >
                {isUploading ? "Uploading image..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
